import { Box } from '@mui/material';
import { SWR_ORDER_IMAGES_ENDPOINT } from 'hooks/swr/useOrderImages';
import { isArray, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useId, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { mutate } from 'swr';
import Dropzone from '../../../core/components/Dropzone';
import UploadProgress from '../../../core/components/UploadProgress';
import { useOrderPageStore } from '../store/useOrderPageStore';
import { resizeImages, uploadFiles } from './orderImageUtils';
import type { FC } from 'react';

const UploadDropZone: FC = observer(() => {
  const [fileUploads, setFileUploads] = useState<
    Array<{ file: File; progress: number; controller: AbortController }>
  >([]);

  const orderPageStore = useOrderPageStore();

  const componentId = useId();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      void resizeImages(acceptedFiles)
        .then((f) => {
          const newUploads = f.map((file) => ({
            file,
            progress: 0,
            controller: new AbortController(),
          }));

          setFileUploads((prevState) => [...prevState, ...newUploads]);

          return newUploads;
        })
        .then(async (f) => {
          await uploadFiles(f, orderPageStore.order?.id ?? 0, (percentCompleted, i) => {
            setFileUploads((prevState) => {
              const updatedUploads = [...prevState];

              const item = updatedUploads[i];
              !isNil(item) && (item.progress = percentCompleted);
              return updatedUploads;
            });
          });

          return true;
        })
        .then(async () => {
          setFileUploads([]);

          return await mutate(
            (key) => isArray(key) && key[0] === SWR_ORDER_IMAGES_ENDPOINT,
            undefined
          );
        });
    },
    [orderPageStore.order]
  );

  const handleCancelUpload = (file: File): void => {
    const uploadToCancel = fileUploads.find((upload) => upload.file === file);
    if (uploadToCancel) {
      uploadToCancel.controller.abort();
      setFileUploads((prevState) => prevState.filter((item) => item !== uploadToCancel));
    }
  };

  const state = useDropzone({ accept: { 'image/*': [] }, onDrop });

  if (fileUploads.length > 0) {
    return (
      <>
        {fileUploads.map((upload, index) => (
          <Box key={`${componentId}-uploader-${index}`}>
            <UploadProgress
              progress={upload.progress}
              fileName={upload.file.name}
              onCancel={() => {
                handleCancelUpload(upload.file);
              }}
            />
          </Box>
        ))}
      </>
    );
  }

  return <Dropzone dropzoneState={state} />;
});

export default UploadDropZone;
